import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { PlanRatesApi } from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { useToast } from "@shared/hooks/use-toast";
import { tarrifFilters } from "./types";

// Add your feature-specific hooks here
export const useOnboardingPlanRates = () => {
  // Define your hooks
  return {};
};

export const useRecentlyUpdatedRates = (limit: number = 3) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.recentlyUpdatedRates(limit),
    () => PlanRatesApi.getRecentlyUpdatedRates(remoteUtilityId, limit),
    { enabled }
  );
};

export const useRecentlyUpdatedPlans = (limit = 3) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.recentlyUpdatedPlans(limit),
    () => PlanRatesApi.getRecentlyUpdatedPlans(remoteUtilityId, limit),
    { enabled }
  );
};

export const usePlanDashboard = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.planKPIs(),
    () => PlanRatesApi.getPlanDashboardData(remoteUtilityId),
    { enabled }
  );
};

export const useUtilityServices = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.utilityServices(),
    () => PlanRatesApi.getUtilityServices(remoteUtilityId),
    {
      enabled,
      refetchOnMount: "always",
      staleTime: 0,
    }
  );
};

export const useTarrifList = (
  filters: tarrifFilters = {},
  remoteUtilityId: string
) => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.planTarrif.rateTable(filters);

  const enabled = !!remote_utility_id;
  return useSmartQuery(
    key as unknown[],
    () => PlanRatesApi.getTarrifList(remote_utility_id, filters),
    { enabled } // ✅ Only run when ID is ready
  );
};

export const useUpdateRate = (id: string) => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: any): Promise<any> => PlanRatesApi.updateRate(data, id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate Updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });
        queryClient.refetchQueries({
          queryKey: [ "rateDetail", id],
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.onboarding.planRates.versionDetail(id),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update rate",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleRateMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: { id: number; is_active: boolean }): Promise<any> =>
      PlanRatesApi.updateRate(data, data.id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate status updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to toggle rate status",
          variant: "destructive",
        });
      },
    }
  );
};

export const useCloneRate = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (id: number): Promise<any> =>
      PlanRatesApi.cloneRate(id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate cloned successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to clone rate",
          variant: "destructive",
        });
      },
    }
  );
};

export const useAddRate = () => {
  return useSmartMutation(
    (data: any): Promise<any> => PlanRatesApi.addRate(data)
  );
};

export const useBulkUpdateRate = () => {
  return useSmartMutation(
    (data: any): Promise<any> => PlanRatesApi.bulkUpdateRate(data)
  );
};

export const useRateTypes = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.rateTypes(),
    () => PlanRatesApi.getRateTypes()
  );
};

export const useRateDetail = (id: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.rateDetail(id),
    () => PlanRatesApi.getRateDetail(id, remoteUtilityId),
    { enabled }
  );
};

export const useRateVersion = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.versionDetail(id),
    () => PlanRatesApi.getVersionDetail(id),
    { enabled:!!id }
  );
};

export const useRateStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.rateStatus(),
    () => PlanRatesApi.getRateStatus()
  );
};



